'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { addDays, differenceInDays } from 'date-fns'
import { formatDayMonth, formatDayMonthYear, toLocalDate } from '@/lib/date-display'
import { CalendarIcon, Users, Search, Minus, Plus, Loader2, MapPin, Tag, X, Check, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useBooking } from '@/contexts/BookingContext'
import { useTranslations, useLocale } from 'next-intl'

interface SearchFormProps {
  cities?: string[]
  initialCouponCode?: string
}

/**
 * Search form for booking website
 * Includes date range picker, city selector, and guest selector
 */
export function SearchForm({ cities = [], initialCouponCode }: SearchFormProps) {
  const t = useTranslations('booking.search')
  const locale = useLocale()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { setSearchState, tenantId } = useBooking()

  // Parse initial dates from URL or use defaults
  const [checkIn, setCheckIn] = useState<string | undefined>(
    searchParams.get('checkIn') || undefined
  )
  const [checkOut, setCheckOut] = useState<string | undefined>(
    searchParams.get('checkOut') || undefined
  )
  const [adults, setAdults] = useState<number>(
    parseInt(searchParams.get('adults') || '2', 10)
  )
  const [children, setChildren] = useState<number>(
    parseInt(searchParams.get('children') || '0', 10)
  )
  const [selectedCity, setSelectedCity] = useState<string>(
    searchParams.get('city') || 'all'
  )

  const [couponCode, setCouponCode] = useState<string>(
    searchParams.get('couponCode') || initialCouponCode || ''
  )
  const [showPromoField, setShowPromoField] = useState(
    !!(searchParams.get('couponCode') || initialCouponCode)
  )
  const [couponStatus, setCouponStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle')
  const [couponInfo, setCouponInfo] = useState<{ discountType?: string; discountValue?: number } | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const validateCouponCode = useCallback(async (code: string) => {
    if (!code || code.length < 3 || !tenantId) {
      setCouponStatus('idle')
      setCouponInfo(null)
      return
    }
    setCouponStatus('validating')
    try {
      const res = await fetch(`/api/booking/validate-coupon?code=${encodeURIComponent(code)}&tenantId=${tenantId}`)
      const data = await res.json()
      if (data.valid) {
        setCouponStatus('valid')
        setCouponInfo({ discountType: data.discountType, discountValue: data.discountValue })
      } else {
        setCouponStatus('invalid')
        setCouponInfo(null)
      }
    } catch {
      setCouponStatus('idle')
      setCouponInfo(null)
    }
  }, [tenantId])

  const handleCouponChange = (value: string) => {
    const upper = value.toUpperCase()
    setCouponCode(upper)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!upper || upper.length < 3) {
      setCouponStatus('idle')
      setCouponInfo(null)
      return
    }
    debounceRef.current = setTimeout(() => validateCouponCode(upper), 500)
  }

  // Validate on mount if coupon code exists from URL
  useEffect(() => {
    if (couponCode && couponCode.length >= 3) {
      validateCouponCode(couponCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A single-city tenant gets no city dropdown — there is nothing to choose.
  const showCityFilter = cities.length >= 2

  const [isDateOpen, setIsDateOpen] = useState(false)
  const [isGuestOpen, setIsGuestOpen] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  // Handle date selection
  const handleDateSelect = (isoDate: string) => {
    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection
      setCheckIn(isoDate)
      setCheckOut(undefined)
    } else {
      // Complete selection
      const startDate = new Date(checkIn)
      const endDate = new Date(isoDate)

      if (endDate > startDate) {
        setCheckOut(isoDate)
        setIsDateOpen(false)
      } else {
        // Clicked date is before start, swap
        setCheckIn(isoDate)
        setCheckOut(checkIn)
        setIsDateOpen(false)
      }
    }
  }

  // Handle search
  const handleSearch = () => {
    if (!checkIn || !checkOut || !tenantId) {
      return
    }

    setIsSearching(true)

    // Update context
    setSearchState({
      checkIn,
      checkOut,
      adults,
      children,
      selectedPropertyId: undefined,
    })

    // Build URL params - now using tenantId for multi-property search
    const params = new URLSearchParams()
    params.set('tenantId', tenantId)
    params.set('checkIn', checkIn)
    params.set('checkOut', checkOut)
    params.set('adults', adults.toString())
    params.set('children', children.toString())
    if (selectedCity && selectedCity !== 'all') {
      params.set('city', selectedCity)
    }
    const trimmedCoupon = couponCode.trim()
    if (trimmedCoupon) {
      params.set('couponCode', trimmedCoupon)
    }

    // Navigate to search - will show all properties, user selects one
    router.push(`/booking/search?${params.toString()}`)
  }

  const totalGuests = adults + children
  const nights = checkIn && checkOut ? differenceInDays(new Date(checkOut), new Date(checkIn)) : 0

  // Locale-aware date formatters — date-fns-based (NOT Intl): this text is in the
  // SSR HTML and Intl 'ar' output differs between WebKit and Node (hydration mismatch).
  const fmtShort = { format: (d: string | Date) => formatDayMonth(d, locale) }
  const fmtFull = { format: (d: string | Date) => formatDayMonthYear(d, locale) }

  const fmtISO = (d: Date) => {
    const y = d.getFullYear()
    const m = `${d.getMonth() + 1}`.padStart(2, '0')
    const day = `${d.getDate()}`.padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  const minDate = fmtISO(new Date())
  const maxDate = fmtISO(addDays(new Date(), 365))

  return (
    <div className="space-y-3">
      {/* Main search row */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Date Range Picker */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('checkInOut')}
          </label>
          <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center w-full h-12 px-4 rounded-md text-left font-normal',
                  'bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600',
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500',
                  'transition-colors duration-150',
                  'text-gray-900 dark:text-gray-100',
                  !checkIn && 'text-gray-500 dark:text-gray-400'
                )}
              >
                <CalendarIcon className="me-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                {checkIn && checkOut ? (
                  <span>
                    {fmtShort.format(checkIn)} &ndash;{' '}
                    {fmtFull.format(checkOut)}
                    <span className="text-gray-500 dark:text-gray-400 ms-1">
                      ({t('nightsCount', { count: nights })})
                    </span>
                  </span>
                ) : checkIn ? (
                  <span>{fmtFull.format(checkIn)} &ndash; {t('selectCheckOut')}</span>
                ) : (
                  <span>{t('selectDates')}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3 border-b">
                <p className="text-sm text-muted-foreground">
                  {!checkIn
                    ? t('selectCheckIn')
                    : !checkOut
                      ? t('selectCheckOut')
                      : t('clickToChangeDates')}
                </p>
              </div>
              <Calendar
                month={checkIn ? toLocalDate(checkIn) : new Date()}
                rangeStart={checkIn}
                rangeEnd={checkOut}
                onChange={handleDateSelect}
                minDate={minDate}
                maxDate={maxDate}
                locale={locale}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* City Selector — only shown when 2+ cities */}
        {showCityFilter && (
          <div className="lg:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('city')}
            </label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger
                className={cn(
                  'h-12 bg-white dark:bg-gray-800/50 border-gray-300 dark:border-gray-600',
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500',
                  'text-gray-900 dark:text-gray-100'
                )}
              >
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
                  <SelectValue placeholder={t('allCities')} />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allCities')}</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Guest Selector */}
        <div className="lg:w-48">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {t('guests')}
          </label>
          <Popover open={isGuestOpen} onOpenChange={setIsGuestOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center w-full h-12 px-4 rounded-md text-left font-normal',
                  'bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600',
                  'hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-400 dark:hover:border-gray-500',
                  'transition-colors duration-150',
                  'text-gray-900 dark:text-gray-100'
                )}
              >
                <Users className="mr-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span>
                  {t('guestCount', { count: totalGuests })}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="start">
              <div className="space-y-4">
                {/* Adults */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('adults')}</p>
                    <p className="text-sm text-muted-foreground">{t('ages13Plus')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      disabled={adults <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{adults}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setAdults(Math.min(10, adults + 1))}
                      disabled={adults >= 10}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Children */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('children')}</p>
                    <p className="text-sm text-muted-foreground">{t('ages0to12')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setChildren(Math.max(0, children - 1))}
                      disabled={children <= 0}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{children}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setChildren(Math.min(10, children + 1))}
                      disabled={children >= 10}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={() => setIsGuestOpen(false)}
                >
                  {t('done')}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Search Button */}
        <div className="lg:shrink-0">
          <div className="hidden lg:block text-sm font-medium mb-1.5">&nbsp;</div>
          <Button
            className="w-full lg:w-auto h-12"
            onClick={handleSearch}
            disabled={!checkIn || !checkOut || !tenantId || isSearching}
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            {isSearching ? t('searching') : t('searchBtn')}
          </Button>
        </div>
      </div>

      {/* Promo Code — right-aligned below search row */}
      <div className="flex justify-end items-center gap-2">
        {!showPromoField ? (
          <button
            type="button"
            onClick={() => setShowPromoField(true)}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors flex items-center gap-1"
          >
            <Tag className="h-3 w-3" />
            {t('havePromoCode')}
          </button>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <Tag className="h-3 w-3 text-gray-400 shrink-0" />
              <div className="relative">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => handleCouponChange(e.target.value)}
                  placeholder={t('enterPromoCode')}
                  autoFocus
                  className={cn(
                    'w-28 h-7 px-2 pe-7 text-xs uppercase rounded border outline-none transition-colors',
                    'bg-white dark:bg-gray-800/50 placeholder:text-gray-400',
                    'text-gray-900 dark:text-gray-100',
                    couponStatus === 'valid'
                      ? 'border-green-400 dark:border-green-600'
                      : couponStatus === 'invalid'
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600 focus:border-gray-400 dark:focus:border-gray-500'
                  )}
                />
                {/* Inline status icon */}
                <span className="absolute end-1.5 top-1/2 -translate-y-1/2">
                  {couponStatus === 'validating' && (
                    <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                  )}
                  {couponStatus === 'valid' && (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  )}
                  {couponStatus === 'invalid' && (
                    <AlertCircle className="h-3.5 w-3.5 text-red-400" />
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => { setCouponCode(''); setCouponStatus('idle'); setCouponInfo(null); setShowPromoField(false) }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Validation message */}
            {couponStatus === 'valid' && couponInfo && (
              <span className="text-xs text-green-600 dark:text-green-400">
                {couponInfo.discountType === 'percentage'
                  ? `${couponInfo.discountValue}% off`
                  : `${couponInfo.discountValue} SAR off`}
              </span>
            )}
            {couponStatus === 'invalid' && couponCode.length >= 3 && (
              <span className="text-xs text-red-400">
                {t('promoCodeInvalid')}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}
